import { useCallback, useEffect, useState } from 'react';
import type { AjusteCanales } from '../../api/ops/entregas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Casilla } from '../../componentes/Casilla';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { miles } from '../plan/logica';
import type { PropsPantalla } from '../tipos';
import { AVISO_CONSUMO_WHATSAPP, PERMISO_CANALES_DE_ENVIO, PERMISO_PLAN_VER, diaDelPeriodo, textoEncendido } from './reglas';
import { AYUDA_CANALES_DE_ENVIO } from './textos';
import './canalesDeEnvio.css';

/**
 * «Canales de envío» (5.8, `mensajes_canales`; G562, G569 a G571, G703): el interruptor «Enviar por WhatsApp», apagado por
 * defecto, con el aviso de consumo siempre a la vista. Encender abre una ventana con la casilla obligatoria «Entiendo…»
 * (`whatsapp_consumption_acknowledged`); apagar es al momento con «Deshacer» 5 s (G159: el cambio sale a los 5 s). Saldo del
 * periodo y «Ver Plan» solo con `plan.ver` (corrección 5.1); «Ir a Canales» sin WhatsApp conectado. Solo lectura: el
 * interruptor quieto (`x-read-only-mode: blocked`).
 */
export function CanalesDeEnvio(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, yo } = props;
    const permisos = usePermisos();
    const puede = permisos.puede(PERMISO_CANALES_DE_ENVIO);
    const verPlan = permisos.puede(PERMISO_PLAN_VER);
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [datos, setDatos] = useState<AjusteCanales | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [nombres, setNombres] = useState<ReadonlyMap<string, string>>(new Map());
    const [confirmar, setConfirmar] = useState(false);
    const [entendido, setEntendido] = useState(false);
    const [guardando, setGuardando] = useState(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.getDeliveryChannelSettings(negocio)
            .then((a) => {
                if (vivo) {
                    setDatos(a);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        // Quién lo encendió: el nombre de ahora, si se puede leer el equipo; si no, sin nombre.
        ops.listMembers(negocio, { limit: 100 })
            .then((r) => { if (vivo) { setNombres(new Map(r.data.map((m) => [m.id, m.display_name]))); } })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, intento]);

    useCabecera({ ayuda: puede ? AYUDA_CANALES_DE_ENVIO : [], acciones: [] });

    if (!puede) {
        return <EstadoSinPermiso seccion="Canales de envío" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && datos === null) {
        return <EstadoError seccion="los canales de envío" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="los canales de envío" />;
    }

    const fallo = (e: unknown) => {
        const breve = avisoBreveDeError(e);
        avisar({ tipo: 'error', texto: breve ?? (e instanceof ErrorApi && e.estado === 412 ? 'Otra persona lo ha cambiado. Te enseñamos lo último.' : 'No he podido guardar el cambio. Prueba de nuevo.') });
        recargar();
    };

    const encender = () => {
        if (!entendido || guardando) {
            return;
        }
        setGuardando(true);
        ops.updateDeliveryChannelSettings(negocio, { whatsapp_enabled: true, whatsapp_consumption_acknowledged: true }, datos.version)
            .then((a) => {
                setDatos(a);
                setConfirmar(false);
                avisar({ tipo: 'ok', texto: 'Enviar por WhatsApp encendido.' });
            })
            .catch((e: unknown) => {
                setConfirmar(false);
                fallo(e);
            })
            .finally(() => { setGuardando(false); });
    };

    const apagar = () => {
        const antes = datos;
        setDatos({ ...datos, whatsapp_enabled: false });
        envio.programar({
            texto: 'Enviar por WhatsApp apagado. Tus mensajes salen por el siguiente canal del cliente o por email.',
            donde: 'ajuste',
            enviar: () => {
                ops.updateDeliveryChannelSettings(negocio, { whatsapp_enabled: false }, antes.version)
                    .then((a) => { setDatos(a); })
                    .catch(fallo);
            },
            alDeshacer: () => {
                setDatos(antes);
            },
        });
    };

    const encendido = datos.whatsapp_enabled;
    const quien = datos.whatsapp_enabled_by_member_id == null
        ? null
        : datos.whatsapp_enabled_by_member_id === membresia?.member_id ? yo.account.display_name : nombres.get(datos.whatsapp_enabled_by_member_id) ?? null;
    const saldo = datos.whatsapp_balance ?? null;
    const conectado = datos.whatsapp_channel_connected;
    const hasta = saldo?.period_end == null ? null : diaDelPeriodo(saldo.period_end);

    return (
        <div className="rv-ca-apartado rv-ce">
            {envio.pendiente !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa"><Icono nombre="message-circle" /></span>
                    <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                    <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                </div>
            )}
            <section className="rv-ce__tarjeta">
                <Interruptor
                    etiqueta="Enviar por WhatsApp"
                    ayuda="Confirmaciones, pases, recordatorios y avisos a tus clientes"
                    icono="message-circle"
                    valor={encendido}
                    desactivado={soloLectura}
                    linea="abajo"
                    onChange={(v) => {
                        if (soloLectura) {
                            return;
                        }
                        if (v) {
                            setEntendido(false);
                            setConfirmar(true);
                        } else {
                            apagar();
                        }
                    }}
                />
                <p className="rv-ce__estado">
                    <Icono nombre={encendido ? 'circle-check' : 'toggle-left'} />
                    <span>{encendido ? textoEncendido(quien, datos.whatsapp_enabled_at ?? null) : 'Apagado. Nada sale por WhatsApp: ni confirmaciones, ni pases, ni recordatorios, ni reenvíos. Se usa el siguiente canal del cliente o el email.'}</span>
                </p>
                <div role="note" className="rv-ce__aviso">
                    <span className="rv-ce__baldosa"><Icono nombre="wallet" /></span>
                    <span>{AVISO_CONSUMO_WHATSAPP}</span>
                </div>
                {verPlan && saldo !== null && (
                    <div className="rv-ce__saldo">
                        <div className="rv-ce__saldo-cab">
                            <span className="rv-ce__eyebrow">Tus mensajes normales</span>
                            {hasta !== null && <span>{`Hasta el ${hasta}`}</span>}
                        </div>
                        <dl className="rv-ce__cifras">
                            <div><dt>Incluidos en tu plan</dt><dd>{miles(saldo.included)}</dd></div>
                            <div><dt>Gastados</dt><dd>{miles(saldo.used)}</dd></div>
                            <div><dt>Quedan en paquetes extra</dt><dd>{miles(saldo.extra_remaining)}</dd></div>
                        </dl>
                    </div>
                )}
                {verPlan && saldo === null && <p className="rv-ce__sin-saldo">Ahora no podemos leer tu saldo de WhatsApp. El ajuste funciona igual.</p>}
                <div className="rv-ce__conexion">
                    <Icono nombre={conectado === false ? 'triangle-alert' : 'circle-check'} />
                    <span className="rv-ce__conexion-texto">
                        {conectado === false
                            ? 'No tienes WhatsApp conectado. Puedes dejarlo encendido: no saldrá nada por WhatsApp hasta que lo conectes en Canales.'
                            : conectado === true ? 'Tienes WhatsApp conectado.' : 'Ahora no podemos saber si tienes WhatsApp conectado.'}
                    </span>
                    {(conectado === false || (verPlan && saldo !== null)) && (
                        <span className="rv-ce__enlaces">
                            {conectado === false && <Boton tipo="secundario" icono="chevron-right" movil={movil} onClick={() => { navegar('/canales'); }}>Ir a Canales</Boton>}
                            {verPlan && saldo !== null && <Boton tipo="secundario" icono="chevron-right" movil={movil} onClick={() => { navegar('/plan?apartado=consumo'); }}>Ver Plan</Boton>}
                        </span>
                    )}
                </div>
            </section>
            <ul className="rv-ce__hechos">
                <li><Icono nombre="user" /><span>Cada cliente sigue eligiendo su canal y sus bajas en su ficha de Clientes.</span></li>
                <li><Icono nombre="send" /><span>Instagram y Telegram no llevan ajuste: salen si son el canal que prefiere el cliente y están conectados.</span></li>
                <li><Icono nombre="megaphone" /><span>Las campañas van aparte: este ajuste no les afecta.</span></li>
            </ul>
            <Ventana
                abierta={confirmar}
                titulo="Enviar por WhatsApp"
                texto="Tus confirmaciones, pases, recordatorios y avisos saldrán por WhatsApp cuando sea el canal que prefiere el cliente."
                accion="Encender"
                accionIcono="message-circle"
                inactiva={!entendido}
                ocupado={guardando}
                movil={movil}
                onAccion={encender}
                onCerrar={() => { setConfirmar(false); }}
            >
                <div className="rv-ce__ventana">
                    <div role="note" className="rv-ce__aviso rv-ce__aviso--ventana">
                        <Icono nombre="wallet" />
                        <span>{AVISO_CONSUMO_WHATSAPP}</span>
                    </div>
                    <Casilla marcada={entendido} onCambio={setEntendido}>Entiendo que cada envío por WhatsApp consume mensajes de mi paquete</Casilla>
                </div>
            </Ventana>
        </div>
    );
}
